import React, { useEffect, useRef, useState } from 'react';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';
import API_URLS from '../config/apiConfig';

const InlineEditorComponent = ({ value, onChange }) => {
    const editorRef = useRef(null); // Reference to the container
    const editorInstanceRef = useRef(null); // Reference to the CKEditor instance
    
    const CustomUploadAdapter = (loader) => ({
        upload: async () => {
            const data = new FormData();
            const file = await loader.file;
            data.append('upload', file);

            try {
                const response = await fetch(API_URLS.CKEDITOR_UPLOAD, {
                    method: 'POST',
                    body: data,
                });

                const result = await response.json();

                if (result.uploaded) {
                    return { default: result.url }; // Return uploaded image URL
                } else {
                    throw new Error('Image upload failed');
                }
            } catch (error) {
                console.error('Error uploading image:', error);
                throw error;
            }
        },
        abort: () => {
            console.log('Image upload aborted');
        },
    });
    
    // Initialize CKEditor
    useEffect(() => {
        let isEditorDestroyed = false;
    
        // Ensure any previous editor instance is destroyed
        if (editorInstanceRef.current) {
            editorInstanceRef.current
                .destroy()
                .then(() => {
                    isEditorDestroyed = true;
                })
                .catch((error) => console.error('Error destroying CKEditor:', error));
        } else {
            isEditorDestroyed = true;
        }
    
        // Create new editor instance only if previous instance was successfully destroyed
        const initializeEditor = () => {
            if (editorRef.current && isEditorDestroyed) {
                InlineEditor.create(editorRef.current, {
                    licenseKey: 'GPL',
                    height:'auto',  
                  //  resize_enabled: false, 
                    image: {
                        resizeOptions: [
                            { name: 'resizeImage:original', label: 'Default image width', value: null },
                            { name: 'resizeImage:50', label: '50% page width', value: '50' },
                            { name: 'resizeImage:75', label: '75% page width', value: '75' },
                        ],
                        toolbar: [
                            'imageTextAlternative',
                            'toggleImageCaption',
                            '|',
                            'imageStyle:inline',
                            'imageStyle:wrapText',
                            'imageStyle:breakText',
                            '|',
                            'resizeImage',
                        ],
                    },
                })
                    .then((editor) => {
                        editorInstanceRef.current = editor;
    
                        // Set the custom upload adapter
                        editor.plugins.get('FileRepository').createUploadAdapter = (loader) =>
                            CustomUploadAdapter(loader);
    
                        // Set initial value
                        if (value !== undefined) {
                            editor.setData(value);
                        }
    
                        // Listen for changes and trigger onChange
                        editor.model.document.on('change:data', () => {
                            const editorData = editor.getData();
                            if (editorData !== value) {
                                onChange(editorData);
                            }
                            adjustEditorHeight(editor);
                        });
    
                        adjustEditorHeight(editor); // Adjust height on initialization
                    })
                    .catch((error) => {
                        console.error('Error initializing CKEditor:', error);
                    });
            }
        };
        
        // Delay initialization slightly to ensure cleanup is complete
        const timeout = setTimeout(initializeEditor, 0);
    
        return () => {
            clearTimeout(timeout); // Cancel any pending initialization
            if (editorInstanceRef.current) {
                editorInstanceRef.current
                    .destroy()
                    .catch((error) => console.error('Error destroying CKEditor during cleanup:', error));
                editorInstanceRef.current = null;
            }
        };
    }, []); // Run only once on mount
     // Run only once on mount

    const adjustEditorHeight = (editor) => {
        const editorElement = editor.ui.view.editable.element;
        editorElement.style.height = 'auto'; // Reset height
        editorElement.style.height = `${editorElement.scrollHeight}px`; // Set height based on content
    };

    // Listen for dynamic `value` prop updates
    useEffect(() => {
        if (editorInstanceRef.current) {
            const currentEditorData = editorInstanceRef.current.getData();
            if (value !== undefined && value !== currentEditorData) {
                editorInstanceRef.current.setData(value);
                adjustEditorHeight(editorInstanceRef.current);
            }
        }
    }, [value]); // Runs only when `value` changes

    return (
        <div
            ref={editorRef}
            className="form-control"
            style={{
                border: '1px solid #ddd',
                padding: '10px',
                minHeight: '200px',
                maxHeight: '500px',
                width: '100%'
            }}
        />
    );
};

export default InlineEditorComponent;
